/**
 * Canais desconectados, dentro da conversa com a VecchIA (pedido de
 * 17/09/2026).
 *
 * - A VecchIA **avisa e pergunta**: reconectar só começa no clique em
 *   "Reconectar" (regra 8 — nada muda sem a pessoa dizer).
 * - **WhatsApp**: o QR aparece no próprio chat, com a mesma espera do
 *   pareamento de Configurações (`usePareamento`).
 * - **E-mail**: os campos da conexão aparecem no chat; salvar guarda a
 *   credencial cifrada e o servidor testa o login SMTP. A senha vai direto ao
 *   cofre — não passa pela IA nem pela conversa.
 * - Os outros provedores ainda se reconectam em Configurações › Integrações.
 *
 * No visual do Brain (classes `brain-*`), como o resto do chat.
 */

import { useEffect, useId, useState } from 'react';

import { Icon } from '@ds/core';
import { usePareamento } from '@/comuns/usePareamento';
import { estadoDoCanal, provedorDeCanal } from '@/data/integracoes';
import { dados } from '@/data/porta';
import type { CanalDeIntegracao } from '@/data/tipos';
import { s } from './classes';
import { Selo } from './ui/camadas';

/** Os canais ativos que não estão no ar, e que a VecchIA sabe reconectar ou apontar. */
export function canaisParaReconectar(canais: CanalDeIntegracao[]): CanalDeIntegracao[] {
    return canais.filter((c) => c.ativo !== false && c.status !== 'conectado');
}

export type ModoDeReconexao = 'whatsapp' | 'email' | 'configuracoes';

export function modoDeReconexao(canal: CanalDeIntegracao, pareamentoDisponivel: boolean): ModoDeReconexao {
    if (canal.provedor === 'uazapi' && pareamentoDisponivel) return 'whatsapp';
    if (canal.provedor === 'smtp') return 'email';
    return 'configuracoes';
}

type Etapa = 'pergunta' | 'reconectando' | 'conectado' | 'dispensado';

export interface CartaoDeCanalProps {
    canal: CanalDeIntegracao;
    pareamentoDisponivel: boolean;
    /** Motivo de não poder agir (impersonação). Presente, não há botão. */
    somenteLeitura: string | null;
    /** Já começa reconectando: a pessoa confirmou a proposta da VecchIA. */
    jaConfirmado?: boolean;
    aoConectar?: (canal: CanalDeIntegracao) => void;
}

export function CartaoDeCanal({ canal, pareamentoDisponivel, somenteLeitura, jaConfirmado = false, aoConectar }: CartaoDeCanalProps) {
    const [etapa, setEtapa] = useState<Etapa>(canal.status === 'conectado' ? 'conectado' : jaConfirmado ? 'reconectando' : 'pergunta');
    const modo = modoDeReconexao(canal, pareamentoDisponivel);
    const estado = estadoDoCanal(canal);
    const conectou = () => {
        setEtapa('conectado');
        aoConectar?.(canal);
    };

    return (
        <section className={`brain-acao ${etapa === 'pergunta' ? 'brain-acao--pendente' : ''}`} aria-label={`Canal ${canal.nome}`}>
            <div className="brain-acao__topo">
                <span className="brain-acao__icone">
                    <Icon name={etapa === 'conectado' ? 'circle-check' : canal.tipo === 'email' ? 'mail' : 'message-circle'} size={14} />
                </span>
                <div className="brain-acao__titulos">
                    <strong className="brain-acao__resumo">{canal.nome}</strong>
                    <span className="brain-acao__meta">{provedorDeCanal(canal.provedor)?.nome ?? canal.provedor}</span>
                </div>
                <Selo tamanho="sm">{etapa === 'conectado' ? 'Conectado' : estado.rotulo}</Selo>
            </div>

            {etapa === 'pergunta' && (
                <>
                    <p className="brain-acao__desfecho">
                        {canal.status === 'erro' && canal.ultimoErro
                            ? `Este canal está com erro: ${canal.ultimoErro}`
                            : 'Este canal está desconectado: as mensagens por ele não saem nem chegam.'}
                        {modo === 'configuracoes' ? ' Por aqui ainda não consigo reconectá-lo — faça isso em Configurações › Integrações.' : ' Quer reconectar agora?'}
                    </p>
                    {somenteLeitura && <p className="brain-acao__desfecho">{somenteLeitura}</p>}
                    {!somenteLeitura && modo !== 'configuracoes' && (
                        <div className="brain-acao__botoes">
                            <button type="button" className={s.btnPrimary} onClick={() => setEtapa('reconectando')} aria-label={`Reconectar ${canal.nome}`}>
                                <Icon name="plug" size={14} />
                                Reconectar
                            </button>
                            <button type="button" className={s.btnGhost} onClick={() => setEtapa('dispensado')} aria-label={`Agora não: ${canal.nome}`}>
                                Agora não
                            </button>
                        </div>
                    )}
                </>
            )}

            {etapa === 'reconectando' && modo === 'whatsapp' && (
                <QrNoChat canal={canal} aoConectar={conectou} aoCancelar={() => setEtapa('pergunta')} />
            )}
            {etapa === 'reconectando' && modo === 'email' && (
                <EmailNoChat canal={canal} aoConectar={conectou} aoCancelar={() => setEtapa('pergunta')} />
            )}
            {etapa === 'reconectando' && modo === 'configuracoes' && (
                <p className="brain-acao__desfecho">Este canal se reconecta em Configurações › Integrações.</p>
            )}

            {etapa === 'conectado' && (
                <p className="brain-acao__desfecho brain-acao__desfecho--ok" role="status">
                    Deu certo: {canal.nome} está conectado{canal.tipo === 'email'
                        ? '. O servidor de e-mail aceitou o login.'
                        : ' e a Comunicação já envia e recebe por ele.'}
                </p>
            )}
            {etapa === 'dispensado' && (
                <p className="brain-acao__desfecho">Tudo bem, deixei como está. Quando quiser, é só pedir para reconectar.</p>
            )}
        </section>
    );
}

function QrNoChat({ canal, aoConectar, aoCancelar }: { canal: CanalDeIntegracao; aoConectar: () => void; aoCancelar: () => void }) {
    const p = usePareamento(canal.id, null, aoConectar);
    const { gerar } = p;

    /* O primeiro código sai sozinho: o "Reconectar" já foi o pedido. */
    useEffect(() => {
        void gerar(canal.temCredencial ? 'conectar' : 'criar');
    }, [gerar, canal.temCredencial]);

    if (p.conectado) return null;
    return (
        <div className="brain-canal__qr">
            <ol className="brain-canal__passos">
                <li>No celular, abra o WhatsApp que vai atender.</li>
                <li>Toque em <strong>Aparelhos conectados</strong> › <strong>Conectar aparelho</strong>.</li>
                <li>Aponte a câmera para o código.</li>
            </ol>
            <div className="brain-canal__codigo">
                {p.temQr ? (
                    <img src={p.estado?.qrcode} alt={`QR Code para conectar ${canal.nome}`} />
                ) : (
                    <span>
                        {p.gerando ? 'Gerando o código…' : p.estado ? 'O código expirou. Gere outro.' : 'Preparando o código…'}
                    </span>
                )}
            </div>
            {/* A contagem muda a cada segundo: leitor de tela não precisa ouvir cada uma. */}
            <p className="brain-acao__desfecho" aria-live="off">
                {p.temQr ? `Esperando a leitura · o código vence em ${p.restante}s` : ''}
            </p>
            {p.estado?.paircode && (
                <p className="brain-acao__desfecho">
                    Ou digite o código <strong>{p.estado.paircode}</strong> em <em>Conectar com número de telefone</em>.
                </p>
            )}
            {p.estado?.aviso && <p className="brain-acao__desfecho">{p.estado.aviso}</p>}
            {p.erro && <p role="alert" className="brain-acao__desfecho brain-acao__desfecho--falha">Não deu para falar com o servidor: {p.erro}</p>}
            {p.falhas.seguidas >= 3 && (
                <p role="alert" className="brain-acao__desfecho brain-acao__desfecho--falha">
                    O servidor do WhatsApp não está respondendo ({p.falhas.motivo}). Sigo perguntando.
                </p>
            )}
            <div className="brain-acao__botoes">
                {!p.temQr && (
                    <button type="button" className={s.btnPrimary} disabled={p.gerando} onClick={() => { void gerar(p.estado ? 'conectar' : 'criar'); }}>
                        <Icon name={p.gerando ? 'loader-circle' : 'qr-code'} size={14} />
                        Gerar outro código
                    </button>
                )}
                <button type="button" className={s.btnGhost} onClick={aoCancelar}>Cancelar</button>
            </div>
        </div>
    );
}

function EmailNoChat({ canal, aoConectar, aoCancelar }: { canal: CanalDeIntegracao; aoConectar: () => void; aoCancelar: () => void }) {
    const prefixo = useId();
    const campos = provedorDeCanal(canal.provedor)?.campos ?? [];
    const [valores, setValores] = useState<Record<string, string>>({});
    const [testando, setTestando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const faltando = campos.filter((c) => !c.opcional && !valores[c.chave]?.trim());

    const conectar = async () => {
        if (faltando.length) {
            setErro(`Preencha: ${faltando.map((c) => c.rotulo).join(', ')}.`);
            return;
        }
        setTestando(true);
        setErro(null);
        try {
            await dados().salvarCredencialDoCanal(canal.id, valores);
            const r = await dados().validarCanalDeEmail(canal.id);
            if (r.status === 'conectado') aoConectar();
            else setErro(r.motivo ?? 'O servidor de e-mail não aceitou a conexão.');
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível testar o e-mail.');
        } finally {
            setTestando(false);
        }
    };

    return (
        <form
            className="brain-canal__form"
            onSubmit={(e) => { e.preventDefault(); void conectar(); }}
            aria-label={`Dados do e-mail ${canal.nome}`}
        >
            <p className="brain-acao__desfecho">
                Preencha os dados do servidor de envio (SMTP). Eu testo o login e aviso. A senha vai direto para o cofre — não fica na conversa.
            </p>
            <div className="brain-canal__campos">
                {campos.map((c) => (
                    <label key={c.chave} className={s.field} htmlFor={`${prefixo}-${c.chave}`}>
                        <span className={s.fieldLabel}>{c.rotulo}{c.opcional ? ' (opcional)' : ''}</span>
                        <input
                            id={`${prefixo}-${c.chave}`}
                            className={s.input}
                            type={c.tipo === 'senha' ? 'password' : 'text'}
                            autoComplete={c.tipo === 'senha' ? 'new-password' : 'off'}
                            placeholder={c.ajuda}
                            value={valores[c.chave] ?? ''}
                            onChange={(e) => { setValores((v) => ({ ...v, [c.chave]: e.target.value })); setErro(null); }}
                        />
                    </label>
                ))}
            </div>
            {erro && <p role="alert" className="brain-acao__desfecho brain-acao__desfecho--falha">{erro}</p>}
            <div className="brain-acao__botoes">
                <button type="submit" className={s.btnPrimary} disabled={testando}>
                    <Icon name={testando ? 'loader-circle' : 'plug'} size={14} />
                    {testando ? 'Testando…' : 'Salvar e conectar'}
                </button>
                <button type="button" className={s.btnGhost} onClick={aoCancelar} disabled={testando}>Cancelar</button>
            </div>
        </form>
    );
}

/** O cartão que segue a proposta `propor_reconectar_canal` confirmada. */
export function CanalDaProposta({ canalId, somenteLeitura }: { canalId: string; somenteLeitura: string | null }) {
    const [canal, setCanal] = useState<CanalDeIntegracao | null>(null);
    const [pareamento, setPareamento] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        let vivo = true;
        Promise.all([dados().canaisDaClinica(), dados().pareamentoDisponivel().catch(() => false)])
            .then(([lista, disponivel]) => {
                if (!vivo) return;
                const achado = lista.find((c) => c.id === canalId) ?? null;
                setCanal(achado);
                setPareamento(disponivel);
                if (!achado) setErro('O canal não foi encontrado: pode ter sido removido.');
            })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler o canal.'); });
        return () => { vivo = false; };
    }, [canalId]);

    if (erro) return <p role="alert" className="brain-acao__desfecho brain-acao__desfecho--falha">{erro}</p>;
    if (!canal) return null;
    return <CartaoDeCanal canal={canal} pareamentoDisponivel={pareamento} somenteLeitura={somenteLeitura} jaConfirmado />;
}

/**
 * O aviso do começo da conversa: a VecchIA olha os canais e, se algum caiu,
 * pergunta. Sem canal caído, não aparece nada.
 */
export function AvisoDeCanais({ somenteLeitura }: { somenteLeitura: string | null }) {
    const [canais, setCanais] = useState<CanalDeIntegracao[]>([]);
    const [pareamento, setPareamento] = useState(false);

    useEffect(() => {
        if (somenteLeitura) return;
        let vivo = true;
        Promise.all([dados().canaisDaClinica(), dados().pareamentoDisponivel().catch(() => false)])
            .then(([lista, disponivel]) => {
                if (!vivo) return;
                setCanais(canaisParaReconectar(lista));
                setPareamento(disponivel);
            })
            /* Sem acesso aos canais, a VecchIA simplesmente não avisa: a conversa segue. */
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [somenteLeitura]);

    if (!canais.length) return null;
    return (
        /* Rola por dentro quando falta altura; os botões dos cartões levam o foco até lá. */
        <div className="brain-canais" role="region" aria-label="Canais desconectados">
            <p className="brain-canais__aviso">
                <Icon name="plug-zap" size={14} />
                {canais.length === 1 ? 'Um canal de atendimento está fora do ar.' : `${canais.length} canais de atendimento estão fora do ar.`}
            </p>
            {canais.map((c) => (
                <CartaoDeCanal key={c.id} canal={c} pareamentoDisponivel={pareamento} somenteLeitura={somenteLeitura} />
            ))}
        </div>
    );
}
